import {
  Attraction,
  CallState,
  DurationRefinement,
  ExperienceAvailability,
  ExperienceId,
  ExperiencesAvailabilityAutocompleteResponse,
  ExperiencesAvailabilityAutocompleteResult,
  ExperiencesAvailabilityRequestEnum,
  ExperiencesAvailabilityResponse,
  ExperiencesAvailabilitySort,
  ExperienceTag,
  ExperienceTagCount,
  ICategorizedResponse,
  PriceRefinement,
  Prices,
  SearchPageToken,
  StartTimeRefinement,
  TripadvisorRatingRefinement,
} from "redmond";
import { actions, actionTypes } from "../actions";
import type { ViewPreference } from "../actions/actions";

export enum ExperiencesAvailabilityCallState {
  NotCalled = "NotCalled",
  InitialSearchCallInProcess = "InitialSearchCallInProcess",
  InitialFilteredSearchCallInProcess = "InitialFilteredSearchCallInProcess",
  FollowUpSearchCallInProcess = "FollowUpSearchCallInProcess",
  InitialSearchCallSuccess = "InitialSearchCallSuccess",
  FollowUpSearchCallSuccess = "FollowUpSearchCallSuccess",
  // when a FollowUpSearch returns empty
  Complete = "Complete",
  Failed = "Failed",
}

export interface IExperiencesAvailabilityFilterState {
  tripAdvisorRating?: TripadvisorRatingRefinement;
  duration?: DurationRefinement[];
  startTime?: StartTimeRefinement[];
  price?: PriceRefinement;
  tags?: ExperienceTag[];
  popularFeatures?: ExperienceTag[];
  sort: ExperiencesAvailabilitySort;
}

export interface IExperiencesAvailabilityState
  extends IExperiencesAvailabilityFilterState {
  availabilityResponse?: ExperiencesAvailabilityResponse;
  // all experiences
  availabilityExperiences: ExperienceAvailability[];
  // map of experience id to experience object
  experiencesMap: Map<string, ExperienceAvailability>;
  // map of experiences to the category they belong in
  experiencesByCategoryMap: { [id: number]: ExperienceId[] };
  categoryTags: ExperienceTagCount[];
  // Initial category tags from unfiltered "Best of" request - used for filter pill display
  initialCategoryTags: ExperienceTagCount[];
  // Initial attraction tags from unfiltered "Best of" request - used for filter pill display
  initialAttractionTags: ExperienceTagCount[];
  availabilityNextPageToken?: SearchPageToken;
  experiencesAvailabilityCallState: ExperiencesAvailabilityCallState;
  keyword: ExperiencesAvailabilityAutocompleteResult | null;
  keywordCategories: ICategorizedResponse[];
  filtersChangedSinceLastSearch: boolean;
  priceFiltersChangedSinceLastSearch: boolean;
  autocompleteCallState: CallState;
  autocompleteResults?: ExperiencesAvailabilityAutocompleteResponse;
  isResetting: boolean;
  availabilityMaxPrice?: Prices;
  availabilityMinPrice?: Prices;
  selectedGroupedExperience?: ExperienceTagCount;
  userHasActivitiesBooked: boolean;
  viewPreference: ViewPreference;
  selectedAttraction?: Attraction;
}

export const initialState: IExperiencesAvailabilityState = {
  experiencesMap: new Map(),
  experiencesByCategoryMap: {},
  categoryTags: [],
  initialCategoryTags: [],
  initialAttractionTags: [],
  availabilityResponse: undefined,
  availabilityExperiences: [],
  experiencesAvailabilityCallState: ExperiencesAvailabilityCallState.NotCalled,
  keyword: null,
  keywordCategories: [],
  filtersChangedSinceLastSearch: false,
  priceFiltersChangedSinceLastSearch: false,
  sort: ExperiencesAvailabilitySort.Recommended,
  autocompleteCallState: CallState.NotCalled,
  isResetting: false,
  selectedGroupedExperience: undefined,
  userHasActivitiesBooked: false,
  viewPreference: "grid",
};

export const reducer = (
  state: IExperiencesAvailabilityState = initialState,
  action: actions.ExperiencesAvailabilityActions
): IExperiencesAvailabilityState => {
  switch (action.type) {
    case actionTypes.FETCH_INITIAL_EXPERIENCES_AVAILABILITY:
      return {
        ...state,
        experiencesAvailabilityCallState:
          ExperiencesAvailabilityCallState.InitialSearchCallInProcess,
        filtersChangedSinceLastSearch: false,
        isResetting: false,
        priceFiltersChangedSinceLastSearch: false,
        // Clear initial tags so the response handler repopulates them
        // with the new destination's categories/attractions.
        initialCategoryTags: [],
        initialAttractionTags: [],
        // Clear filters from previous session so initial load is unfiltered
        selectedGroupedExperience: undefined,
        tags: [],
      };
    case actionTypes.FETCH_FILTERED_EXPERIENCES_AVAILABILITY:
      return {
        ...state,
        experiencesAvailabilityCallState:
          ExperiencesAvailabilityCallState.InitialFilteredSearchCallInProcess,
        filtersChangedSinceLastSearch: false,
        isResetting: false,
        priceFiltersChangedSinceLastSearch: false,
      };
    case actionTypes.FETCH_MORE_EXPERIENCES_AVAILABILITY:
      return {
        ...state,
        experiencesAvailabilityCallState:
          ExperiencesAvailabilityCallState.FollowUpSearchCallInProcess,
        filtersChangedSinceLastSearch: false,
        priceFiltersChangedSinceLastSearch: false,
      };
    case actionTypes.SET_EXPERIENCES_AVAILABILITY_RESULTS: {
      if (state.isResetting) {
        return state;
      }

      const isInitialRequest =
        action.responseType ===
        ExperiencesAvailabilityRequestEnum.InitialSearch;
      const hasCompletedRequest =
        !action.payload.nextPageToken ||
        action.payload.experiences.length === 0;
      const resetAvailabilityResponse =
        action.resetAvailabilityResponse ?? true;
      const resetPriceFilterConstraints =
        action.resetPriceFilterConstraints ?? true;

      let newExperiencesMap = state.experiencesMap;
      let newExperiencesByCategoryMap = state.experiencesByCategoryMap;
      let newCategoryTags = state.categoryTags;
      let newInitialCategoryTags = state.initialCategoryTags;
      let newInitialAttractionTags = state.initialAttractionTags;
      let newAvailabilityResponse = state.availabilityResponse;
      let newAvailabilityExperiences = state.availabilityExperiences || [];
      let newCallState = ExperiencesAvailabilityCallState.NotCalled;
      let newAutocompleteResults = state.autocompleteResults;
      let newAutocompleteCallState = state.autocompleteCallState;
      let newMaxPrice = state.availabilityMaxPrice;
      let newMinPrice = state.availabilityMinPrice;
      let newSelectedGroupedExperience = state.selectedGroupedExperience;
      let newTags = state.tags;

      if (isInitialRequest) {
        newExperiencesMap = new Map();
        newExperiencesByCategoryMap = {};
        newAvailabilityExperiences = [];
        newAutocompleteResults = undefined;
        newAutocompleteCallState = CallState.NotCalled;
        // Always update categoryTags on initial requests (original behavior)
        newCategoryTags = action.payload.categoryTags;

        // Populate initial tags only when they're empty (first load or after
        // destination reset). Don't overwrite on subsequent "Best of" re-fetches
        // so that corrected tag counts (from filtered requests) are preserved.
        const shouldPopulateInitialTags =
          state.initialCategoryTags.length === 0 && state.initialAttractionTags.length === 0;

        if (shouldPopulateInitialTags) {
          newInitialCategoryTags = action.payload.categoryTags;
          newInitialAttractionTags = action.payload.attractionTags ?? [];
        }
      }

      if (resetAvailabilityResponse) {
        newAvailabilityResponse = action.payload;
      }

      if (resetPriceFilterConstraints) {
        // The backend returns sentinel values (e.g. Number.MAX_SAFE_INTEGER
        // for maxPrice) when the destination has no activities matching
        // the refinements. Forwarding those into the slider renders a
        // nonsense label like "$9,007,199,254,740,991". Treat an empty
        // response as "no price range" so the filter's truthy guard in
        // the component hides it.
        const hasResults = action.payload.experiences.length > 0;
        newMaxPrice = hasResults ? action.payload.maxPrice : undefined;
        newMinPrice = hasResults ? action.payload.minPrice : undefined;
      }

      if (hasCompletedRequest) {
        newCallState = ExperiencesAvailabilityCallState.Complete;
      } else if (isInitialRequest) {
        newCallState =
          ExperiencesAvailabilityCallState.InitialSearchCallSuccess;
      } else {
        newCallState =
          ExperiencesAvailabilityCallState.FollowUpSearchCallSuccess;
      }

      action.payload.experiences.forEach((item) => {
        newExperiencesMap.set(item.id.value, item);
        const itemTags = item.tags;
        itemTags.forEach((tag) => {
          if (newExperiencesByCategoryMap[tag.id]) {
            newExperiencesByCategoryMap[tag.id].push(item.id);
          } else {
            newExperiencesByCategoryMap[tag.id] = [item.id];
          }
        });
      });

      // When a filtered request completes (all pages loaded), update the
      // tag count in initialAttractionTags/initialCategoryTags to reflect
      // the actual number of activities returned by the API. The backend's
      // pre-computed tag count can include activities not available for the
      // selected dates, causing pill count to exceed the actual grid count.
      if (
        hasCompletedRequest &&
        newSelectedGroupedExperience &&
        newCallState === ExperiencesAvailabilityCallState.Complete
      ) {
        const actualCount = newExperiencesMap.size;
        const selectedTagId = newSelectedGroupedExperience.tag.id;

        newInitialAttractionTags = newInitialAttractionTags.map((t) =>
          t.tag.id === selectedTagId ? { ...t, count: actualCount } : t
        );
        newInitialCategoryTags = newInitialCategoryTags.map((t) =>
          t.tag.id === selectedTagId ? { ...t, count: actualCount } : t
        );
        newSelectedGroupedExperience = {
          ...newSelectedGroupedExperience,
          count: actualCount,
        };
      }

      if (
        action.request.AvailabilityRequest ===
          ExperiencesAvailabilityRequestEnum.InitialSearch &&
        action.request.refinement.tags.length > 0
      ) {
        // Find the tag with an empty name, which indicates that user has accessed the site via a url
        // that has categoryTagId assigned. i.e. experience cross-sell on confirmation page
        const urlTag = action.request.refinement.tags.find(
          (tag) => tag.name === ""
        );
        const tagId = urlTag?.id;
        const isAttractionTag = urlTag?.kind?.ActivityTagKind === "Attraction";

        // If we found a tag with an empty name, set it as the selected grouped experience
        if (tagId) {
          // Check attraction tags first, otherwise check category tags
          if (isAttractionTag && action.payload.attractionTags) {
            newSelectedGroupedExperience = action.payload.attractionTags.find(
              (tag) => tag.tag.id === tagId
            );
          } else if (action.payload.categoryTags) {
            newSelectedGroupedExperience = action.payload.categoryTags.find(
              (tag) => tag.tag.id === tagId
            );
          }

          // add selectedGroupExperience tag so that future sort/filter works on the same category page
          if (newSelectedGroupedExperience?.tag) {
            newTags = [newSelectedGroupedExperience.tag];
            // Preserve the kind from the URL for attraction tags
            if (isAttractionTag) {
              newTags = [{
                ...newSelectedGroupedExperience.tag,
                kind: { ActivityTagKind: "Attraction" as const },
              }];
            }
          }
        }
      }

      return {
        ...state,
        availabilityResponse: newAvailabilityResponse,
        availabilityExperiences: [
          ...newAvailabilityExperiences,
          ...action.payload.experiences,
        ],
        experiencesMap: newExperiencesMap,
        experiencesByCategoryMap: newExperiencesByCategoryMap,
        categoryTags: newCategoryTags,
        initialCategoryTags: newInitialCategoryTags,
        initialAttractionTags: newInitialAttractionTags,
        availabilityNextPageToken: action.payload.nextPageToken,
        experiencesAvailabilityCallState: newCallState,
        autocompleteCallState: newAutocompleteCallState,
        autocompleteResults: newAutocompleteResults,
        availabilityMaxPrice: newMaxPrice,
        availabilityMinPrice: newMinPrice,
        selectedGroupedExperience: newSelectedGroupedExperience,
        tags: newTags,
      };
    }

    case actionTypes.SET_EXPERIENCES_AVAILABILITY_CALL_STATE_FAILED:
      return {
        ...state,
        experiencesAvailabilityCallState:
          ExperiencesAvailabilityCallState.Failed,
      };

    case actionTypes.RESET_EXPERIENCES_AVAILABILITY:
      return {
        ...initialState,
        isResetting: true,
      };

    case actionTypes.SET_KEYWORD: {
      const { keyword } = action;
      return {
        ...state,
        keyword: keyword ? { ...keyword } : null,
      };
    }

    case actionTypes.SET_FILTER_TRIPADVISOR_RATING: {
      return {
        ...state,
        tripAdvisorRating: action.rating,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_START_TIMES: {
      return {
        ...state,
        startTime: action.startTimes,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_DURATION: {
      return {
        ...state,
        duration: action.durationTimes,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_PRICE: {
      return {
        ...state,
        price: action.price,
        filtersChangedSinceLastSearch: false,
        priceFiltersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_TAGS: {
      return {
        ...state,
        tags: action.tags,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_POPULAR_FEATURES: {
      return {
        ...state,
        popularFeatures: action.tags,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_SORT_OPTION: {
      return {
        ...state,
        sort: action.sortOption,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.RESET_FILTER_AND_SORT: {
      return {
        ...state,
        tripAdvisorRating: TripadvisorRatingRefinement.Any,
        duration: [],
        startTime: [],
        price: undefined,
        tags: [],
        popularFeatures: [],
        sort: ExperiencesAvailabilitySort.Recommended,
        selectedGroupedExperience: undefined,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.FETCH_EXPERIENCES_AVAILABILITY_AUTOCOMPLETE: {
      return {
        ...state,
        autocompleteCallState: CallState.InProcess,
      };
    }

    case actionTypes.SET_EXPERIENCES_AVAILABILITY_AUTOCOMPLETE_RESULTS: {
      return {
        ...state,
        autocompleteResults: action.autocompleteResults,
        autocompleteCallState: CallState.Success,
      };
    }

    case actionTypes.SET_EXPERIENCE_TAG_COUNT: {
      return {
        ...state,
        selectedGroupedExperience: action.tag,
      };
    }

    case actionTypes.SET_USER_HAS_BOOKED_ACTIVITIES:
      return {
        ...state,
        userHasActivitiesBooked: action.userHasActivitiesBooked,
      };

    case actionTypes.SET_VIEW_PREFERENCE:
      return {
        ...state,
        viewPreference: action.viewPreference,
      };

    case actionTypes.SET_SELECTED_ATTRACTION:
      return {
        ...state,
        selectedAttraction: action.attraction,
      };

    default:
      return { ...state };
  }
};

export * from "./selectors";
